<template>
  <div v-if="props.modelValue" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog"
    aria-modal="true">
    <!-- Fond sombre avec flou -->
    <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity backdrop-blur-sm"></div>

    <!-- Contenu du modal -->
    <div class="flex min-h-full items-center justify-center p-4">
      <div
        class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
        <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
          <h3 class="text-lg font-bold leading-6 text-gray-900 mb-4">
            📦 Importer un Schéma
          </h3>

          <!-- Étape 1: Sélection fichier -->
          <SchemaImportStep1 v-if="step === 1" :selected-file="selectedFile" :parsing="parsing"
            @file-selected="handleFileSelected" @file-cleared="handleFileCleared" @analyze-file="parseFile" />

          <!-- Étape 2: Validation et édition des métadonnées du schéma -->
          <SchemaImportStep2 v-if="step === 2" :parsed-schema="parsedSchema" :schema-conflict="schemaConflict"
            :editable-schema="editableSchema" :is-schema-valid="isSchemaValid" :validation-errors="validationErrors"
            :valid-schema-types="validSchemaTypes" @update-schema-conflict-action="handleSchemaConflictAction"
            @update:editable-schema="handleEditableSchemaUpdate" />

          <!-- Étape 3: Aperçu et résolution conflits -->
          <SchemaImportStep3 v-if="step === 3" :parsed-schema="parsedSchema" :resolutions="resolutions"
            :translation-resolutions="translationResolutions"
            :new-section-translation-resolutions="newSectionTranslationResolutions"
            :similar-section-choices="similarSectionChoices" :new-section-actions="newSectionActions"
            :keep-removed-sections="keepRemovedSections" :schema-conflict="schemaConflict"
            @update-resolution="handleUpdateResolution"
            @update-translation-resolution="handleUpdateTranslationResolution"
            @update-new-section-translation-resolution="handleUpdateNewSectionTranslationResolution"
            @update-similar-section-choice="handleUpdateSimilarSectionChoice"
            @update-new-section-action="handleUpdateNewSectionAction"
            @update-removed-section-action="handleUpdateRemovedSectionAction" />

          <!-- Étape 4: Résultats -->
          <SchemaImportStep4 v-if="step === 4 && importReport" :import-report="importReport"
            :start-time="importStartTime || undefined" />

          <!-- Message d'erreur -->
          <div v-if="errorMessage" class="bg-red-50 border border-red-200 p-4 rounded-lg">
            <div class="flex">
              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd" />
              </svg>
              <div class="ml-3">
                <h4 class="text-sm font-medium text-red-800">Erreur</h4>
                <p class="text-sm text-red-700 mt-1">{{ errorMessage }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Boutons d'action -->
        <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
          <!-- Étape 1 -->
          <template v-if="step === 1">
            <button
              class="w-full sm:w-auto bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-md transition-colors"
              @click="close">
              Annuler
            </button>
          </template>

          <!-- Étape 2 -->
          <template v-if="step === 2">
            <button :disabled="!isSchemaValid"
              class="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md transition-colors sm:ml-3"
              @click="proceedToAnalysis">
              🔍 Analyser les conflits
            </button>
            <button
              class="mt-3 sm:mt-0 w-full sm:w-auto bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-md transition-colors"
              @click="step = 1">
              Retour
            </button>
          </template>

          <!-- Étape 3 -->
          <template v-if="step === 3">
            <button :disabled="importing || !canCreate"
              class="w-full sm:w-auto bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md transition-colors sm:ml-3"
              @click="executeImportWithTiming">
              <span v-if="importing">🔄 Import en cours...</span>
              <span v-else>🚀 Importer le schéma</span>
            </button>
            <button
              class="mt-3 sm:mt-0 w-full sm:w-auto bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-md transition-colors"
              @click="step = 2">
              Retour
            </button>
          </template>

          <!-- Étape 4 -->
          <template v-if="step === 4">
            <button
              class="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
              @click="close">
              Terminer
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ImportReport } from '@/api/types/core/schemaJson';
import { useAdminPermissions } from '@/composables/useAdminPermissions';
import { ref, watch } from 'vue';
import { useSchemaImport } from '../../composables/useSchemaImport';
import { useSchemaValidation } from '../../composables/useSchemaValidation';
import SchemaImportStep1 from './import/SchemaImportStep1.vue';
import SchemaImportStep2 from './import/SchemaImportStep2.vue';
import SchemaImportStep3 from './import/SchemaImportStep3.vue';
import SchemaImportStep4 from './import/SchemaImportStep4.vue';

// Props & Emits
const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'import-success': [report: ImportReport];
}>();

// Variables pour le suivi du temps
const importStartTime = ref<Date | null>(null);

// Permissions
const { canCreate } = useAdminPermissions();

// Composables
const {
  step,
  selectedFile,
  parsedSchema,
  importReport,
  errorMessage,
  parsing,
  importing,
  schemaConflict,
  editableSchema,
  resolutions,
  translationResolutions,
  newSectionTranslationResolutions,
  similarSectionChoices,
  newSectionActions,
  parseFile,
  executeImport,
  reset,
  updateResolution,
  updateTranslationResolution,
  updateNewSectionTranslationResolution,
  updateSimilarSectionChoice,
  updateNewSectionAction,
  keepRemovedSections,
  updateRemovedSectionAction
} = useSchemaImport();

const {
  isSchemaValid,
  validationErrors,
  validSchemaTypes
} = useSchemaValidation(editableSchema, schemaConflict);

// Gestionnaires d'événements
const handleFileSelected = (file: File): void => {
  selectedFile.value = file;
  errorMessage.value = '';
};

const handleFileCleared = (): void => {
  selectedFile.value = null;
  errorMessage.value = '';
};

const handleSchemaConflictAction = (action: 'update' | 'create_new'): void => {
  if (schemaConflict.value) {
    schemaConflict.value.action = action;
  }
};

const handleEditableSchemaUpdate = (schema: { appellation: string; type: string; description: string }): void => {
  editableSchema.appellation = schema.appellation;
  editableSchema.type = schema.type;
  editableSchema.description = schema.description;
};

// Gestionnaires pour Step3
const handleUpdateResolution = (conflictId: string, resolution: string): void => {
  updateResolution(conflictId, resolution);
};

const handleUpdateTranslationResolution = (sectionName: string, languageCode: string, fieldType: string, resolution: string): void => {
  updateTranslationResolution(sectionName, languageCode, fieldType, resolution);
};

const handleUpdateNewSectionTranslationResolution = (sectionName: string, languageCode: string, resolution: string): void => {
  updateNewSectionTranslationResolution(sectionName, languageCode, resolution);
};

const handleUpdateSimilarSectionChoice = (sectionId: string, action: 'create_new' | 'align_with_existing', targetSection?: string): void => {
  updateSimilarSectionChoice(sectionId, action, targetSection);
};

const handleUpdateNewSectionAction = (sectionId: string, action: 'create' | 'skip'): void => {
  updateNewSectionAction(sectionId, action);
};

const handleUpdateRemovedSectionAction = (sectionId: number, action: 'keep' | 'remove'): void => {
  updateRemovedSectionAction(sectionId, action);
};

const proceedToAnalysis = (): void => {
  if (!parsedSchema.value || !isSchemaValid.value) return;

  // Mettre à jour le schéma parsé avec les valeurs éditées
  parsedSchema.value.appellation = editableSchema.appellation.trim();
  parsedSchema.value.type = editableSchema.type;
  parsedSchema.value.description = editableSchema.description.trim();

  step.value = 3;
};

const executeImportWithTiming = async (): Promise<void> => {
  importStartTime.value = new Date();
  await executeImport();
};

const close = (): void => {
  emit('update:modelValue', false);

  // Réinitialiser après fermeture pour éviter les conflits visuels
  setTimeout(() => {
    reset();
    importStartTime.value = null;
  }, 300);
};

// Watcher pour émettre le succès d'import
watch(importReport, (report) => {
  if (report) {
    emit('import-success', report);
  }
});
</script>